import React, { useEffect, useRef, useState } from 'react';

import Modal from 'src/shared/components/modal/Modal';
import MinimalMarkdown from 'src/shared/components/minimal-markdown/MinimalMarkdown';

export type ReadMoreProps = {
    text?: string | null;
    lines?: number;
    modalTitle?: string;
    className?: string;
    onExpand?: () => void;
    /** Render **bold**, *italic*, line breaks and bullet lists instead of the raw text. Defaults to false. */
    renderAsMarkdown?: boolean;
};

const ReadMore = (props: ReadMoreProps) => {
    const { text, lines = 5, modalTitle = 'Full content', className, onExpand, renderAsMarkdown = false } = props;

    const [isOverflowing, setIsOverflowing] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const clampedRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const update = () => {
            const clamped = clampedRef.current;
            const measure = measureRef.current;
            if (!clamped || !measure) {
                return;
            }
            // Use precise width and copy key text styles to avoid measurement mismatches
            const rect = clamped.getBoundingClientRect();
            measure.style.width = (rect && rect.width ? rect.width : clamped.clientWidth) + 'px';
            const computed = window.getComputedStyle(clamped);
            measure.style.fontSize = computed.fontSize;
            measure.style.fontFamily = computed.fontFamily;
            measure.style.fontWeight = computed.fontWeight as any;
            measure.style.letterSpacing = computed.letterSpacing;
            measure.style.lineHeight = computed.lineHeight;
            measure.style.whiteSpace = 'normal';
            measure.style.wordBreak = computed.wordBreak as any;
            const unclampedHeight = measure.scrollHeight;
            const clampedHeight = clamped.clientHeight;

            setIsOverflowing(unclampedHeight - clampedHeight > 1);
        };

        // Use rAF to ensure layout has settled
        const raf = requestAnimationFrame(update);
        window.addEventListener('resize', update);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', update);
        };
    }, [text, lines]);

    if (!text) {
        return <span className='text-grey-80'>—</span>;
    }

    const lineClampStyles: React.CSSProperties = {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical' as any,
        WebkitLineClamp: lines as any,
        overflow: 'hidden',
    };

    return (
        <div className={['text-sm text-black relative', className].filter(Boolean).join(' ')}>
            <div ref={clampedRef} style={lineClampStyles}>
                {renderAsMarkdown ? <MinimalMarkdown text={text} /> : text}
            </div>
            {/* Hidden measurement element to detect overflow */}
            <div
                ref={measureRef}
                className='absolute invisible pointer-events-none whitespace-normal'
                style={{
                    left: 0,
                    top: 0,
                    zIndex: -1,
                    position: 'absolute',
                    display: 'block',
                    overflow: 'visible',
                }}
            >
                {renderAsMarkdown ? <MinimalMarkdown text={text} /> : text}
            </div>
            {isOverflowing && (
                <button className='text-primary mt-2 inline-block' onClick={() => (onExpand ? onExpand() : setShowModal(true))}>
                    Read more
                </button>
            )}

            {showModal && (
                <Modal onClose={() => setShowModal(false)} dismissOnClickOutside={true} classNames={{ body: 'w-[700px] p-6' }}>
                    <div className='flex flex-col gap-3'>
                        <div className='text-xl text-primary'>{modalTitle}</div>
                        <div className='text-sm text-black whitespace-pre-wrap'>
                            {renderAsMarkdown ? <MinimalMarkdown text={text} /> : text}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ReadMore;
